import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X, Phone, Pencil, Trash2, UserRound, Copy, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { createStaff, updateStaff } from '../../redux/staffSlice';
import { docPatCreationCountryList } from '../../redux/masterSlice';
import PhoneCountryDropdown from '../common/PhoneCountryDropdown';
import GenderSelect from '../common/GenderSelect';
import ModalShell, { ModalFooter, MODAL_SUBMIT_CLASS } from '../common/ModalShell';
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';
import useSession from '../../hooks/useSession';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import { policyFor, policyErrorMessage, policyErrorField, missingAnyContact } from '../../utils/loginPolicy';

// Create / edit a staff member. One modal for both, because the field set is
// identical and the only differences are the title, the endpoint, and whether a
// temporary password comes back.
//
// The image goes in a SECOND call once the id exists, matching AddAdmin — the
// create endpoint takes JSON, not multipart.

const EMPTY = {
  name: '', surname: '', email: '', phone: '', gender: '', dob: '',
  countryId: '', timeZone: '', userRoleId: '', departments: [],
  designation: '', employeeCode: '', registrationNo: '',
};
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

export default function AddUser({ isOpen, onClose, onSaved, editing = null, roles = [] }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const session = useSession();
  const mutating = useSelector((s) => s.staff?.mutating);
  const countryTimezoneList = useSelector((s) => s.master?.countryTimezoneList);

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [deptInput, setDeptInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Edit mode: the admin pressed the trash on the STORED photo (not just a
  // local preview) — submit must then call the real DELETE endpoint
  // (review, 2026-09-02: the backend remove existed with no UI).
  const [removeStored, setRemoveStored] = useState(false);
  // Shown after creating: mail is frequently misconfigured on a fresh
  // deployment, and an admin standing next to the new employee needs to be able
  // to read the password out rather than discover an hour later it never
  // arrived.
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);
  // The photo goes up as multipart in a second call, so it cannot ride the redux
  // thunks. `createAuthedAxios` is the default export of hooks/useAxiosAuth.js —
  // a factory, not a hook, imported under its real name (see usePermissions.js).
  const axiosInstance = useMemo(() => createAuthedAxios(), []);

  // THE TENANT'S LOGIN POLICY (/tenants/public) decides which contacts are
  // mandatory — the e-mail when `email.required`, the mobile when
  // `mobile.required`, at least one of the two under 'either' (anyOne). Nothing
  // is e-mail-only any more (owner, 1 Oct 2026, "Concept 2"): under "Mobile no
  // only" a staff member is created with a number and no e-mail. No OTP step
  // here — the code would go to the new person's phone, not the admin's. The
  // server sends the login id(s) and a temporary password; at the FIRST sign-in
  // the person proves each contact with a code and chooses a password.
  const [publicTenants, setPublicTenants] = useState([]);
  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance.get('/tenants/public');
        if (alive) setPublicTenants(res.data?.tenants || []);
      } catch {
        if (alive) setPublicTenants([]);
      }
    })();
    return () => { alive = false; };
  }, [isOpen, axiosInstance]);
  const loginPolicy = policyFor(publicTenants, session.tenantId || '');

  const isEdit = Boolean(editing);
  const selectedCountry = countryTimezoneList?.find((c) => c.countryId === form.countryId) || null;
  // Only active roles can be assigned. Assigning an inactive one creates an
  // account that is blocked from its first login, which looks like a bug in the
  // account rather than a deliberate state of the role.
  const assignableRoles = (roles || []).filter((r) => r.status === 'active' || r.userRoleId === editing?.userRoleId);

  useEffect(() => {
    if (isOpen && (!countryTimezoneList || countryTimezoneList.length === 0)) {
      dispatch(docPatCreationCountryList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setTempPassword(null);
    setCopied(false);
    setErrors({});
    setDeptInput('');
    // Image state never survives between opens (the modal stays mounted), and
    // edit mode starts from the STORED photo — the old code showed an empty
    // circle over an existing photo and leaked a cancelled pick into the next
    // open (review, 2026-09-02). editing.image is already presigned.
    setImageFile(null);
    setImagePreview(editing?.image || null);
    setRemoveStored(false);
    if (editing) {
      setForm({
        name: editing.name || '', surname: editing.surname || '', email: editing.email || '',
        phone: editing.phone || '', gender: editing.gender || '',
        dob: editing.dob ? String(editing.dob).slice(0, 10) : '',
        countryId: editing.countryId || session.tenantCountryId || '',
        timeZone: editing.timeZone || '',
        userRoleId: editing.userRoleId || '', departments: editing.departments || [],
        designation: editing.designation || '', employeeCode: editing.employeeCode || '',
        registrationNo: editing.registrationNo || '',
      });
    } else {
      // Staff inherit the hospital's country, so seed it — otherwise the phone
      // field starts disabled and the admin has to pick a country the backend
      // will overwrite anyway.
      setForm({ ...EMPTY, countryId: session.tenantCountryId || '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editing]);

  useEffect(() => () => { if (imagePreview) URL.revokeObjectURL(imagePreview); }, [imagePreview]);

  if (!isOpen) return null;

  const set = (k, v) => {
    let val = v;
    if (k === 'name') val = formatFirstName(val);
    if (k === 'surname') val = formatSurname(val);
    setForm((f) => ({ ...f, [k]: val }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const addDepartment = () => {
    const d = deptInput.trim();
    if (!d) return;
    if (form.departments.includes(d)) { setDeptInput(''); return; }
    setForm((f) => ({ ...f, departments: [...f.departments, d] }));
    setDeptInput('');
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      toast.warn(t('userpage.invalid_image_type', 'Please select a JPG, PNG or WEBP image'));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.warn(t('userpage.image_too_large', 'Image must be 5MB or smaller'));
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setRemoveStored(false);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    // Trash on the STORED photo (edit mode, no new pick) = delete for real on
    // save; trash on a fresh local pick just clears the pick.
    if (!imageFile && editing?.image) setRemoveStored(true);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = () => {
    const e = {};
    if (!form.userRoleId) e.userRoleId = t('userpage.role_required', 'Pick the role this user will hold');
    if (!form.name.trim()) e.name = t('userpage.name_required', 'Name is required');
    // Each contact is mandatory exactly when the tenant's login policy says so.
    if (!form.email.trim()) {
      if (loginPolicy.email.required) e.email = t('userpage.email_required', 'Email is required');
    } else if (!emailRe.test(form.email.trim())) e.email = t('userpage.email_invalid', 'Enter a valid email');
    if (loginPolicy.mobile.required && !form.phone.trim()) {
      e.phone = t('userpage.phone_required', 'Mobile number is required');
    }
    // 'either': neither field is starred, but one of them must be there.
    if (missingAnyContact(loginPolicy, { hasEmail: !!form.email.trim(), hasPhone: !!form.phone.trim() })) {
      e.email = t('loginPolicy.one_contact_required', 'An e-mail or a mobile number is required');
    }
    if (form.phone.trim()) {
      if (!form.countryId) {
        e.countryId = t('userpage.country_required_for_phone', 'Select a country for the phone number');
      } else {
        const min = selectedCountry?.mobileMinLength || 9;
        const max = selectedCountry?.mobileMaxLength || 10;
        const digits = form.phone.trim();
        if (!/^\d+$/.test(digits) || digits.length < min || digits.length > max) {
          // The same key AddPatient (and AddDoctor's shape) uses for this rule,
          // so a French admin reads it in French (P45).
          e.phone = t('addPatient.invalid_phone_number', { length: min === max ? min : `${min}-${max}` });
        }
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const uploadImage = async (staffId) => {
    if (!imageFile || !staffId) return;
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      await axiosInstance.post(`/staff/${staffId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch {
      toast.warn(t('userpage.image_upload_failed', 'User saved, but the photo could not be uploaded.'));
    }
  };

  const submit = async () => {
    if (!validate()) return;
    const payload = {
      name: form.name.trim(),
      surname: form.surname.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      gender: form.gender || null,
      dob: form.dob || null,
      countryId: form.countryId || null,
      timeZone: form.timeZone || null,
      userRoleId: form.userRoleId,
      departments: form.departments,
      designation: form.designation.trim() || null,
      employeeCode: form.employeeCode.trim() || null,
      registrationNo: form.registrationNo.trim() || null,
    };

    try {
      if (isEdit) {
        await dispatch(updateStaff({ staffId: editing.staffId, data: payload })).unwrap();
        if (removeStored && !imageFile) {
          try {
            await axiosInstance.delete(`/staff/${editing.staffId}/image`);
          } catch {
            toast.warn(t('userpage.image_remove_failed', 'User saved, but the photo could not be removed.'));
          }
        }
        await uploadImage(editing.staffId);
        toast.success(t('userpage.update_success', 'User updated'));
        onClose?.();
        onSaved?.();
        return;
      }

      const result = await dispatch(createStaff(payload)).unwrap();
      const newId = result?.data?.staffId;
      await uploadImage(newId);
      onSaved?.();
      // Hold the modal open on the password panel instead of closing — closing
      // it would throw the one copy of the credentials away.
      setTempPassword(result?.tempPassword || null);
      if (!result?.tempPassword) {
        toast.success(t('userpage.create_success', 'User created'));
        onClose?.();
      }
    } catch (err) {
      const policyMsg = policyErrorMessage(err?.data, t, i18n.language);
      if (policyMsg) {
        setErrors((e) => ({ ...e, [policyErrorField(err.data.field)]: policyMsg }));
        toast.error(policyMsg);
        return;
      }
      toast.error(typeof err?.error === 'string' ? err.error : t('userpage.save_error', 'Failed to save the user'));
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.warn(t('userpage.copy_failed', 'Could not copy — select the text and copy it manually.'));
    }
  };

  const field = 'w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';

  // --- credentials panel, shown after a successful create ------------------
  if (tempPassword) {
    return (
      <ModalShell
        title={t('userpage.user_created', 'User created')}
        size="md"
        zClass="z-[9999]"
        testId="add-user-created"
        footer={(
          <button type="button" onClick={() => { setTempPassword(null); onClose?.(); }}
            data-testid="add-user-done"
            className={MODAL_SUBMIT_CLASS}>
            {t('userpage.done', 'Done')}
          </button>
        )}
      >
        <div className="text-center">
          <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center">
            <Check className="w-7 h-7" />
          </div>
          <p className="text-sm text-slate-500 mb-5">
            {t('userpage.created_hint', 'The sign-in details were sent to them. Copy them now in case the message does not arrive.')}
          </p>
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-left space-y-2 mb-5">
            <div className="text-xs text-slate-500">{t('userpage.username', 'Username')}</div>
            <div className="text-sm font-mono text-gray-800 break-all">{form.email.trim() || form.phone.trim()}</div>
            <div className="text-xs text-slate-500 pt-2">{t('userpage.temp_password', 'Temporary password')}</div>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono text-gray-800 bg-white px-2 py-1 rounded border border-gray-200 flex-1 break-all">{tempPassword}</code>
              <button type="button" onClick={copyPassword} data-testid="copy-temp-password"
                className="p-2 rounded-lg text-[#4ABDBD] hover:bg-[#4ABDBD]/10" title={t('userpage.copy', 'Copy')}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title={isEdit ? t('userpage.edit_user', 'Edit user') : t('userpage.add_user', 'Add user')}
      onClose={onClose}
      size="lg"
      zClass="z-[9999]"
      testId="add-user-modal"
      closeTestId="add-user-close"
      footer={(
        <ModalFooter
          onCancel={onClose}
          cancelLabel={t('userpage.cancel', 'Cancel')}
          onSubmit={submit}
          submitLabel={isEdit ? t('userpage.save', 'Save changes') : t('userpage.create', 'Create user')}
          busy={mutating}
          submitTestId="add-user-submit"
        />
      )}
    >
        <div className="flex flex-col items-center gap-2 pt-1">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-[#4ABDBD]/40 flex items-center justify-center">
              {imagePreview ? (
                <img src={imagePreview} alt={t('userpage.avatar_alt', 'Photo preview')} className="w-full h-full object-cover" />
              ) : (
                <UserRound className="w-10 h-10 text-gray-300" />
              )}
            </div>
            <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="add-user-avatar-edit"
              className="absolute bottom-0 right-0 p-1.5 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow-md hover:shadow-lg">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            {imagePreview && (
              <button type="button" onClick={removeImage}
                className="absolute bottom-0 left-0 p-1.5 rounded-full bg-white text-red-500 border border-gray-200 shadow-md hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
          <span className="text-xs text-slate-400">{t('userpage.photo', 'Photo (optional)')}</span>
        </div>

        <div className="pt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* The role comes first — it is the whole point of the record, and it
              decides what the account can do the moment it exists. */}
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-500">{t('userpage.role', 'Role')} *</label>
            <select data-testid="add-user-role" value={form.userRoleId}
              onChange={(e) => set('userRoleId', e.target.value)}
              className={`tt-select ${field} ${errors.userRoleId ? 'border-red-400' : 'border-gray-300'}`}>
              <option value="">{t('userpage.select_role', 'Select a role')}</option>
              {assignableRoles.map((r) => (
                <option key={r.userRoleId} value={r.userRoleId}>{r.name}</option>
              ))}
            </select>
            {errors.userRoleId
              ? <p className="text-xs text-red-500 mt-1">{errors.userRoleId}</p>
              : <p className="text-[11px] text-slate-400 mt-1">
                  {t('userpage.role_hint', 'The role decides what this person can open and change. Manage roles under Users → Roles.')}
                </p>}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500">{t('userpage.first_name', 'First name')} *</label>
            <input data-testid="add-user-name" value={form.name} onChange={(e) => set('name', e.target.value)}
              className={`${field} ${errors.name ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">{t('userpage.surname', 'Surname')}</label>
            <input data-testid="add-user-surname" value={form.surname} onChange={(e) => set('surname', e.target.value)}
              className={`${field} border-gray-300`} />
          </div>
          <div>
            <label data-testid="add-user-email-label" className="text-xs font-medium text-slate-500">{t('userpage.email', 'Email')}{loginPolicy.email.required ? ' *' : ''}</label>
            <input data-testid="add-user-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
              className={`${field} ${errors.email ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            {loginPolicy.email.otp && (
              <p data-testid="add-user-email-otp-hint" className="text-[11px] text-slate-400 mt-1">{t('loginPolicy.verified_at_first_sign_in', 'They will verify this with a one-time code at their first sign-in.')}</p>
            )}
          </div>
          <div>
            <label data-testid="add-user-phone-label" className="text-xs font-medium text-slate-500">{t('userpage.phone', 'Phone')}{loginPolicy.mobile.required ? ' *' : ''}</label>
            <div className={`relative flex items-center border rounded-xl px-3 py-2.5 ${!form.countryId ? 'bg-gray-50' : 'bg-white'} ${errors.phone ? 'border-red-400' : 'border-gray-300'} focus-within:ring-2 focus-within:ring-[#4ABDBD]`}>
              <Phone className={`w-4 h-4 mr-2 flex-shrink-0 ${!form.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
              <PhoneCountryDropdown
                countryTimezoneList={countryTimezoneList || []}
                countryId={form.countryId}
                onSelect={({ countryId, timeZone }) => {
                  setForm((f) => ({ ...f, countryId, timeZone, phone: '' }));
                  setErrors((e) => ({ ...e, countryId: undefined, phone: undefined }));
                }}
              />
              <input data-testid="add-user-phone" type="tel" value={form.phone}
                onChange={(e) => set('phone', e.target.value.replace(/\D/g, ''))}
                maxLength={selectedCountry?.mobileMaxLength || 10}
                disabled={!form.countryId}
                className="w-full bg-transparent focus:outline-none text-sm text-gray-800 disabled:cursor-not-allowed" />
            </div>
            {errors.countryId && <p className="text-xs text-red-500 mt-1">{errors.countryId}</p>}
            {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
            {loginPolicy.mobile.otp && (
              <p data-testid="add-user-phone-otp-hint" className="text-[11px] text-slate-400 mt-1">{t('loginPolicy.verified_at_first_sign_in', 'They will verify this with a one-time code at their first sign-in.')}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">{t('userpage.gender', 'Gender')}</label>
            <GenderSelect value={form.gender} onChange={(val) => set('gender', val)}
              options={getGenderOptions(t)} placeholder={t('userpage.select', 'Select')} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">{t('userpage.dob', 'Date of birth')}</label>
            <input data-testid="add-user-dob" type="date" value={form.dob} onChange={(e) => set('dob', e.target.value)}
              max={new Date().toISOString().split('T')[0]} className={`${field} border-gray-300`} />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500">{t('userpage.designation', 'Designation')}</label>
            <input data-testid="add-user-designation" value={form.designation} onChange={(e) => set('designation', e.target.value)}
              className={`${field} border-gray-300`} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">{t('userpage.employee_code', 'Employee code')}</label>
            <input data-testid="add-user-employee-code" value={form.employeeCode} onChange={(e) => set('employeeCode', e.target.value)}
              className={`${field} border-gray-300`} />
          </div>

          {/* A dispensing pharmacist and a lab technician are individually
              registered, and the number has to appear on what they sign. */}
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-500">
              {t('userpage.registration_no', 'Council / registration number')}
            </label>
            <input data-testid="add-user-registration" value={form.registrationNo} onChange={(e) => set('registrationNo', e.target.value)}
              className={`${field} border-gray-300`} />
            <p className="text-[11px] text-slate-400 mt-1">
              {t('userpage.registration_hint', 'For pharmacists, lab and imaging staff — it appears on what they sign.')}
            </p>
          </div>

          {/* WHERE they work, as opposed to WHAT they may do. One person can be
              posted to more than one counter or section. */}
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-500">{t('userpage.departments', 'Departments / counters')}</label>
            <div className="flex gap-2">
              <input
                data-testid="add-user-department-input"
                value={deptInput}
                onChange={(e) => setDeptInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDepartment(); } }}
                placeholder={t('userpage.department_placeholder', 'e.g. OP Counter — press Enter')}
                className={`${field} border-gray-300`}
              />
              <button type="button" onClick={addDepartment} data-testid="add-user-department-add"
                className="px-4 py-2 text-sm rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold hover:bg-[#4ABDBD]/10 whitespace-nowrap">
                {t('userpage.add', 'Add')}
              </button>
            </div>
            {form.departments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.departments.map((d) => (
                  <span key={d} className="inline-flex items-center gap-1.5 bg-[#4ABDBD]/10 text-[#2D8A8A] px-2.5 py-1 rounded-lg text-xs font-semibold">
                    {d}
                    <button type="button" onClick={() => setForm((f) => ({ ...f, departments: f.departments.filter((x) => x !== d) }))}
                      className="text-[#2D8A8A]/60 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

    </ModalShell>
  );
}
